interface Options {
  prefersReducedMotion: boolean;
}

export class AccordionAnimation {
  public static readonly contentClassName = 'accordion-content';
  public static readonly summaryClassName = 'accordion-summary';

  private readonly _accordionOpenClass = 'accordion-open';

  private _animationConfig: KeyframeAnimationOptions;
  private _animation: Animation | null;
  private _content: HTMLElement | null;
  private _element: HTMLDetailsElement;
  private _isClosing: boolean;
  private _isExpanding: boolean;
  private _summary: HTMLElement | null;

  constructor(element: HTMLDetailsElement, options: Options) {
    this._animationConfig = {
      duration: options.prefersReducedMotion ? 0 : 300,
      easing: 'ease-out',
    };

    this._animation = null;
    this._content = element.querySelector(`.${AccordionAnimation.contentClassName}`);
    this._element = element;
    this._isClosing = false;
    this._isExpanding = false;
    this._summary = element.querySelector(`.${AccordionAnimation.summaryClassName}`);
    this._summary?.addEventListener('click', this._onClick);
  }

  public removeEventListener = () => {
    this._summary?.removeEventListener('click', this._onClick);
  };

  private _onClick = (event: MouseEvent) => {
    event.preventDefault();

    this._element.style.overflow = 'hidden';

    if (this._isClosing || !this._element.open) {
      this._open();
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    else if (this._isExpanding || this._element.open) {
      this._shrink();
    }
  };

  private _shrink = () => {
    this._isClosing = true;

    const startHeight = `${this._element.offsetHeight.toString()}px`;
    const endHeight = this._summary ? `${this._summary.offsetHeight.toString()}px` : '';

    if (this._animation) {
      this._animation.cancel();
    }

    this._element.classList.remove(this._accordionOpenClass);

    this._animation = this._element.animate({
      height: [startHeight, endHeight],
    }, this._animationConfig);

    this._animation.onfinish = () => {
      this._onAnimationFinish(false);
    };

    this._animation.oncancel = () => this._isClosing = false;
  };

  private _open = () => {
    this._element.style.height = `${this._element.offsetHeight.toString()}px`;
    this._element.open = true;

    window.requestAnimationFrame(() => {
      this._expand();
    });
  };

  private _expand = () => {
    this._isExpanding = true;
    const startHeight = `${this._element.offsetHeight.toString()}px`;
    const endHeight = `${((this._summary?.offsetHeight ?? 0) + (this._content?.offsetHeight ?? 0)).toString()}px`;

    if (this._animation) {
      this._animation.cancel();
    }

    this._element.classList.add(this._accordionOpenClass);

    this._animation = this._element.animate({
      height: [startHeight, endHeight],
    }, this._animationConfig);

    this._animation.onfinish = () => {
      this._onAnimationFinish(true);
    };

    this._animation.oncancel = () => this._isExpanding = false;
  };

  private _onAnimationFinish = (isOpen: boolean) => {
    this._element.open = isOpen;
    this._animation = null;
    this._isClosing = false;
    this._isExpanding = false;
    this._element.style.height = this._element.style.overflow = '';
  };
}
